/// <reference types="google.maps" />

'use client'

import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Text,
  VStack
} from '@chakra-ui/react'
import { MdClose, MdMyLocation, MdSearch } from 'react-icons/md'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useDebouncedCallback } from 'ui'
import type { MapLocatorLabels, MapLocatorSearchSettings } from 'ecosystem'
import type { LocatorOrigin } from './useMapLocator'
import { MAP_LOCATOR_CN, mapLocatorCn as cn } from './constants'

interface PlaceSearchProps {
  search: MapLocatorSearchSettings
  labels: Required<MapLocatorLabels>
  countryBias?: string[]
  query: string
  onQueryChange: (value: string) => void
  onOriginChange: (origin: LocatorOrigin | null) => void
  origin: LocatorOrigin | null
}

interface Suggestion {
  id: string
  primary: string
  secondary?: string
  prediction: google.maps.places.PlacePrediction
}

const SUGGESTION_DEBOUNCE_MS = 250

/**
 * Uses the headless Places API (New) so the input keeps the store's own styling.
 * If the new API is not enabled on the key, autocomplete silently steps down to
 * geocoding the typed text on submit.
 */
const PlaceSearch = ({
  search,
  labels,
  countryBias,
  query,
  onQueryChange,
  onOriginChange,
  origin
}: PlaceSearchProps) => {
  const placesLib = useMapsLibrary('places')
  const geocodingLib = useMapsLibrary('geocoding')

  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [highlighted, setHighlighted] = useState(-1)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  const autocompleteSupported = Boolean(
    search.autocomplete && placesLib && 'AutocompleteSuggestion' in placesLib
  )

  const geocoder = useMemo(
    () => (geocodingLib ? new geocodingLib.Geocoder() : null),
    [geocodingLib]
  )

  const resetSuggestions = useCallback(() => {
    setSuggestions([])
    setHighlighted(-1)
  }, [])

  const fetchSuggestions = useDebouncedCallback(async (input: string) => {
    if (!autocompleteSupported || !placesLib || input.trim().length < 2) {
      resetSuggestions()

      return
    }

    try {
      sessionToken.current ||= new placesLib.AutocompleteSessionToken()

      const { suggestions: results } =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionToken.current,
          ...(countryBias?.length ? { includedRegionCodes: countryBias.slice(0, 15) } : {})
        })

      setSuggestions(
        results
          .map((result, index) => {
            const prediction = result.placePrediction

            if (!prediction) {
              return null
            }

            return {
              id: prediction.placeId || `suggestion-${index}`,
              primary: prediction.mainText?.toString() || prediction.text.toString(),
              secondary: prediction.secondaryText?.toString(),
              prediction
            }
          })
          .filter(Boolean) as Suggestion[]
      )
      setHighlighted(-1)
    } catch {
      // A disabled Places API (New) must not break the widget — the geocoding
      // fallback on submit still resolves whatever the visitor typed.
      resetSuggestions()
    }
  }, SUGGESTION_DEBOUNCE_MS)

  const handleChange = useCallback(
    (value: string) => {
      setError(null)
      onQueryChange(value)

      if (!value.trim()) {
        resetSuggestions()
        onOriginChange(null)
      } else {
        void fetchSuggestions(value)
      }
    },
    [onQueryChange, fetchSuggestions, resetSuggestions, onOriginChange]
  )

  const selectSuggestion = useCallback(
    async (suggestion: Suggestion) => {
      resetSuggestions()

      try {
        const place = suggestion.prediction.toPlace()

        await place.fetchFields({ fields: ['location', 'displayName', 'formattedAddress'] })

        const location = place.location

        if (!location) {
          return
        }

        // A new token must be issued once a session ends with a place selection
        sessionToken.current = null
        onQueryChange('')
        onOriginChange({
          lat: location.lat(),
          lng: location.lng(),
          label: place.displayName || suggestion.primary
        })
      } catch {
        setError(labels.geolocationUnavailable)
      }
    },
    [resetSuggestions, onOriginChange, onQueryChange, labels.geolocationUnavailable]
  )

  const geocodeQuery = useCallback(async () => {
    if (!geocoder || !query.trim()) {
      return
    }

    try {
      const { results } = await geocoder.geocode({
        address: query,
        ...(countryBias?.length === 1 ? { componentRestrictions: { country: countryBias[0] } } : {})
      })

      const best = results[0]

      if (best) {
        // The resolved place becomes the filter; leaving the typed text in place
        // would keep narrowing the list by name on top of it.
        onQueryChange('')
        onOriginChange({
          lat: best.geometry.location.lat(),
          lng: best.geometry.location.lng(),
          label: best.formatted_address
        })
      }
    } catch {
      setError(labels.geolocationUnavailable)
    }
  }, [geocoder, query, countryBias, onOriginChange, onQueryChange, labels.geolocationUnavailable])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        resetSuggestions()

        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlighted((current) => Math.min(current + 1, suggestions.length - 1))

        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlighted((current) => Math.max(current - 1, -1))

        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()

        const selected = suggestions[highlighted]

        if (selected) {
          void selectSuggestion(selected)
        } else {
          void geocodeQuery()
        }
      }
    },
    [suggestions, highlighted, selectSuggestion, geocodeQuery, resetSuggestions]
  )

  /** Geolocation is only ever requested from this click — never on mount. */
  const handleFindMe = useCallback(() => {
    setError(null)

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError(labels.geolocationUnavailable)

      return
    }

    setLocating(true)

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocating(false)
        onQueryChange('')
        resetSuggestions()
        onOriginChange({ lat: coords.latitude, lng: coords.longitude, label: labels.findMe })
      },
      (positionError) => {
        setLocating(false)
        setError(
          positionError.code === positionError.PERMISSION_DENIED
            ? labels.geolocationDenied
            : labels.geolocationUnavailable
        )
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    )
  }, [labels, onOriginChange, onQueryChange, resetSuggestions])

  const handleClear = useCallback(() => {
    onQueryChange('')
    onOriginChange(null)
    resetSuggestions()
    setError(null)
  }, [onQueryChange, onOriginChange, resetSuggestions])

  useEffect(() => resetSuggestions, [resetSuggestions])

  if (!search.enabled) {
    return null
  }

  const hasInput = Boolean(query || origin)

  return (
    <VStack align="stretch" className={cn(MAP_LOCATOR_CN.SEARCH)} spacing={1} w="full">
      <HStack spacing={2} w="full">
        <Box position="relative" w="full">
          <InputGroup>
            <Input
              aria-autocomplete={autocompleteSupported ? 'list' : undefined}
              aria-label={search.placeholder || labels.searchPlaceholder}
              autoComplete="off"
              onChange={(event) => handleChange(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                origin?.label ? origin.label : search.placeholder || labels.searchPlaceholder
              }
              value={query}
            />

            <InputRightElement>
              {hasInput ? (
                <IconButton
                  aria-label={labels.clearFilters}
                  icon={<MdClose />}
                  onClick={handleClear}
                  size="sm"
                  variant="ghost"
                />
              ) : (
                <MdSearch />
              )}
            </InputRightElement>
          </InputGroup>

          {suggestions.length ? (
            <List
              bg="white"
              borderWidth="1px"
              boxShadow="md"
              left={0}
              maxH="260px"
              overflowY="auto"
              position="absolute"
              right={0}
              rounded="md"
              top="calc(100% + 4px)"
              zIndex={20}
            >
              {suggestions.map((suggestion, index) => (
                <ListItem
                  aria-selected={index === highlighted}
                  bg={index === highlighted ? 'blackAlpha.50' : undefined}
                  cursor="pointer"
                  key={suggestion.id}
                  onClick={() => void selectSuggestion(suggestion)}
                  onMouseEnter={() => setHighlighted(index)}
                  px={3}
                  py={2}
                  role="option"
                  _hover={{ bg: 'blackAlpha.50' }}
                >
                  <Text fontSize="sm">{suggestion.primary}</Text>

                  {suggestion.secondary ? (
                    <Text color="gray.500" fontSize="xs">
                      {suggestion.secondary}
                    </Text>
                  ) : null}
                </ListItem>
              ))}
            </List>
          ) : null}
        </Box>

        {search.showFindMe ? (
          <Button
            flexShrink={0}
            isLoading={locating}
            leftIcon={<MdMyLocation />}
            loadingText={labels.findMeLoading}
            onClick={handleFindMe}
            variant="outline"
          >
            {labels.findMe}
          </Button>
        ) : null}
      </HStack>

      {error ? (
        <Text color="red.500" fontSize="sm" role="alert">
          {error}
        </Text>
      ) : null}
    </VStack>
  )
}

export default PlaceSearch
